// src/pages/ApiDocs.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';

// ─── Layout ──────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2.5rem 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 2.5rem;
`;

// ─── Section ─────────────────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
`;

const SectionText = styled.p`
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.7;
  margin-bottom: 0.75rem;
`;

// ─── Endpoint cards ───────────────────────────────────────────────────────────

const EndpointGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EndpointCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: white;
`;

const EndpointHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  background: white;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover { background: #f9fafb; }
`;

const MethodBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  min-width: 52px;
  text-align: center;

  ${({ method }) => {
    if (method === 'GET')    return 'background:#dcfce7;color:#166534;';
    if (method === 'POST')   return 'background:#dbeafe;color:#1d4ed8;';
    if (method === 'DELETE') return 'background:#fee2e2;color:#991b1b;';
    if (method === 'PATCH')  return 'background:#fef3c7;color:#92400e;';
    return 'background:#f3f4f6;color:#374151;';
  }}
`;

const EndpointPath = styled.code`
  font-size: 0.875rem;
  color: #1f2937;
  font-family: 'Courier New', monospace;
  flex: 1;
`;

const EndpointDesc = styled.span`
  font-size: 0.825rem;
  color: #6b7280;
  margin-left: auto;
`;

const Chevron = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  transition: transform 0.15s;
  transform: ${({ open }) => open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const EndpointBody = styled.div`
  border-top: 1px solid #f3f4f6;
  padding: 1rem 1.25rem;
  background: #fafafa;
`;

const BodyText = styled.p`
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0.75rem;
  line-height: 1.6;
`;

// ─── Code blocks ─────────────────────────────────────────────────────────────

const CodeBlock = styled.div`
  background: #1e1e2e;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.75rem;
`;

const CodeLabel = styled.div`
  background: #2a2a3e;
  color: #a0a0b8;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.35rem 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CodePre = styled.pre`
  margin: 0;
  padding: 1rem;
  font-size: 0.8rem;
  font-family: 'Courier New', monospace;
  color: #cdd6f4;
  overflow-x: auto;
  line-height: 1.6;
  white-space: pre;
`;

// ─── Alert ───────────────────────────────────────────────────────────────────

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  color: #1d4ed8;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const WarnBox = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  color: #92400e;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

// ─── Table ───────────────────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  border: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  color: #374151;
  vertical-align: top;
`;

const Mono = styled.code`
  font-family: 'Courier New', monospace;
  font-size: 0.85em;
  background: #f3f4f6;
  padding: 0.1em 0.3em;
  border-radius: 3px;
`;

// ─── Collapsible endpoint ─────────────────────────────────────────────────────

const Endpoint = ({ method, path, description, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <EndpointCard>
      <EndpointHeader onClick={() => setOpen(o => !o)}>
        <MethodBadge method={method}>{method}</MethodBadge>
        <EndpointPath>{path}</EndpointPath>
        <EndpointDesc>{description}</EndpointDesc>
        <Chevron open={open}>▼</Chevron>
      </EndpointHeader>
      {open && <EndpointBody>{children}</EndpointBody>}
    </EndpointCard>
  );
};

const Code = ({ label, children }) => (
  <CodeBlock>
    {label && <CodeLabel>{label}</CodeLabel>}
    <CodePre>{children}</CodePre>
  </CodeBlock>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const BASE = 'https://your-platform/api';

const ApiDocs = () => {
  const navigate = useNavigate();

  return (
    <PageContainer>
      <Navbar onNavigate={(p) => navigate(p)} />
      <ContentContainer>

        <PageTitle>API Documentation</PageTitle>
        <PageSubtitle>
          Use the REST API to integrate APK analysis into your scripts, CI pipelines, and threat-intel workflows.
        </PageSubtitle>

        {/* ── Authentication ────────────────────────────── */}
        <Section>
          <SectionTitle>Authentication</SectionTitle>
          <SectionText>
            Every endpoint requires authentication. Two methods are supported — use whichever fits your workflow.
          </SectionText>

          <Table>
            <thead>
              <tr>
                <Th>Method</Th>
                <Th>Header</Th>
                <Th>When to use</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td><strong>API Key</strong></Td>
                <Td><Mono>X-API-Key: apk_…</Mono></Td>
                <Td>Scripts, automation, CI/CD — generate keys in your Profile page</Td>
              </tr>
              <tr>
                <Td><strong>JWT Bearer</strong></Td>
                <Td><Mono>Authorization: Bearer &lt;token&gt;</Mono></Td>
                <Td>Browser sessions, short-lived interactive use</Td>
              </tr>
            </tbody>
          </Table>

          <InfoBox>
            Generate and manage your API keys in <strong>Profile → API Keys</strong>.
            The full key is shown exactly once at creation time — copy it immediately.
          </InfoBox>

          <Code label="Get a JWT token (login)">
{`curl -X POST ${BASE}/auth/token \\
  -d "username=you@example.com&password=yourpassword"`}
          </Code>

          <Code label="Use an API key">
{`curl ${BASE}/search/list \\
  -H "X-API-Key: apk_your_key_here"`}
          </Code>
        </Section>

        {/* ── Upload & Analysis ─────────────────────────── */}
        <Section>
          <SectionTitle>Upload &amp; Analysis</SectionTitle>
          <EndpointGrid>

            <Endpoint method="POST" path="/api/files/upload" description="Submit an APK for analysis">
              <BodyText>
                Upload an APK file. If the same SHA256 was seen before, the existing analysis is returned immediately
                (<Mono>is_new_upload: false</Mono>). Otherwise analysis starts asynchronously — poll
                the status endpoint or stream events to track progress.
              </BodyText>
              <Table>
                <thead>
                  <tr><Th>Form field</Th><Th>Type</Th><Th>Description</Th></tr>
                </thead>
                <tbody>
                  <tr><Td><Mono>file</Mono></Td><Td>file</Td><Td>The APK file (required)</Td></tr>
                  <tr><Td><Mono>tags</Mono></Td><Td>string</Td><Td>Comma-separated labels, e.g. <Mono>trojan,virustotal</Mono> (optional)</Td></tr>
                  <tr><Td><Mono>malware_family</Mono></Td><Td>string</Td><Td>Manually set the malware family at upload time (optional). If the SHA256 already exists, this updates the existing analysis's family instead of re-uploading. Once set this way, the auto-detector will never overwrite it.</Td></tr>
                  <tr><Td><Mono>send_to_sandbox</Mono></Td><Td>bool</Td><Td>If <Mono>true</Mono>, automatically submit to dynamic sandbox after static analysis (optional, default: false)</Td></tr>
                </tbody>
              </Table>
              <Code label="curl — basic upload">
{`curl -X POST ${BASE}/files/upload \\
  -H "X-API-Key: apk_your_key_here" \\
  -F "file=@/path/to/sample.apk"`}
              </Code>
              <Code label="curl — upload with tags and auto-sandbox">
{`curl -X POST ${BASE}/files/upload \\
  -H "X-API-Key: apk_your_key_here" \\
  -F "file=@/path/to/sample.apk" \\
  -F "tags=trojan,virustotal" \\
  -F "send_to_sandbox=true"`}
              </Code>
              <Code label="curl — upload with a known malware family">
{`curl -X POST ${BASE}/files/upload \\
  -H "X-API-Key: apk_your_key_here" \\
  -F "file=@/path/to/sample.apk" \\
  -F "malware_family=Cerberus"`}
              </Code>
              <Code label="response">
{`{
  "message": "File uploaded and analyzed successfully",
  "file_id": "a3f1c2d4...",   // SHA256 — use this for all subsequent calls
  "is_new_upload": true
}`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/files/analysis/{sha256}" description="Get full analysis result">
              <BodyText>
                Returns the complete analysis document once analysis is finished, including certificates,
                permissions, activities, intelligence enrichment, and artifacts.
              </BodyText>
              <Code label="curl">
{`curl ${BASE}/files/analysis/a3f1c2d4... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/files/analysis/{sha256}/status" description="Lightweight status check">
              <BodyText>
                Returns only the analysis status and progress — use this to poll without fetching the full document.
              </BodyText>
              <Code label="curl">
{`curl ${BASE}/files/analysis/a3f1c2d4.../status \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`{
  "analysis_status": "completed",   // pending | processing | completed | failed
  "analysis_progress": { "completion": 100 },
  "app_name": "My App",
  "package_name": "com.example.app",
  "malware_family": "BankBot"
}`}
              </Code>
            </Endpoint>

            <Endpoint method="POST" path="/api/files/rescan/{sha256}" description="Trigger a rescan">
              <BodyText>
                Re-runs the full analysis pipeline on an existing sample. Updates <Mono>timestamp</Mono> (last analyzed)
                but never overwrites <Mono>first_upload_time</Mono>.
              </BodyText>
              <Code label="curl">
{`curl -X POST ${BASE}/files/rescan/a3f1c2d4... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/files/download/{sha256}" description="Download the original APK">
              <Code label="curl">
{`curl ${BASE}/files/download/a3f1c2d4... \\
  -H "X-API-Key: apk_your_key_here" \\
  -o sample.apk`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Dynamic Analysis ─────────────────────────── */}
        <Section>
          <SectionTitle>Dynamic Analysis</SectionTitle>
          <SectionText>
            Submit APKs to the behavioral sandbox to observe runtime activity — network connections,
            file system changes, and process spawning. Submission can happen automatically at upload
            time (via <Mono>send_to_sandbox=true</Mono>) or on-demand via the endpoint below.
          </SectionText>
          <EndpointGrid>

            <Endpoint method="POST" path="/api/dynamic-analysis/{sha256}/submit" description="Submit an existing analysis to the sandbox">
              <BodyText>
                Queues the APK for dynamic analysis. Returns <Mono>409</Mono> if already pending or completed.
                The APK file must be available on the server (uploaded previously).
              </BodyText>
              <Code label="curl">
{`curl -X POST ${BASE}/dynamic-analysis/a3f1c2d4.../submit \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`{
  "message": "Successfully submitted for dynamic analysis",
  "status": "pending"
}`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/dynamic-analysis/{sha256}/status" description="Get dynamic analysis status and results">
              <BodyText>
                Returns the current sandbox status and, once completed, the full behavioral report
                including network, filesystem, and process activity.
              </BodyText>
              <Code label="curl">
{`curl ${BASE}/dynamic-analysis/a3f1c2d4.../status \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response — pending">
{`{
  "status": "pending",
  "external_status": "analyzing",
  "submitted_at": "2026-05-13T10:00:00",
  "results": {}
}`}
              </Code>
              <Code label="response — completed">
{`{
  "status": "completed",
  "external_status": "completed",
  "results": {
    "report": {
      "network": { "insights": { "ip": [...], "dns": [...] } },
      "filesystem": { "insights": { "interesting_files": [...] } },
      "processes": { "details": [...] }
    },
    "completed_at": "2026-05-13T10:05:00"
  }
}`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/dynamic-analysis/{sha256}/can-submit" description="Check if sandbox submission is available">
              <BodyText>
                Returns whether the APK file is available on the server and the dynamic analysis service is enabled.
                Useful before showing a "Submit to Sandbox" button in your tooling.
              </BodyText>
              <Code label="curl">
{`curl ${BASE}/dynamic-analysis/a3f1c2d4.../can-submit \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`{ "can_submit": true, "reason": null }
{ "can_submit": false, "reason": "APK file not available on server" }`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Tags ─────────────────────────────────────── */}
        <Section>
          <SectionTitle>Tags</SectionTitle>
          <SectionText>
            Tags are free-form labels attached to analysis documents. A predefined vocabulary is available
            (virustotal, urlscan, trojan, banker, …) but any lowercase string is accepted.
            Tags can be set at upload time or added/removed at any time via the PATCH endpoint.
            They are indexed and searchable via <Mono>/search/list?tag=</Mono> and <Mono>tag:value</Mono> syntax.
          </SectionText>
          <EndpointGrid>

            <Endpoint method="GET" path="/api/files/tags/predefined" description="List the predefined tag vocabulary">
              <BodyText>Returns the list of recommended tags. You are not restricted to this list.</BodyText>
              <Code label="curl">
{`curl ${BASE}/files/tags/predefined \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`{
  "tags": [
    "virustotal", "urlscan", "manual", "ads", "suspicious", "internal"
  ]
}`}
              </Code>
            </Endpoint>

            <Endpoint method="PATCH" path="/api/files/analysis/{sha256}/tags" description="Add or remove tags on an existing analysis">
              <BodyText>
                Send an <Mono>add</Mono> list and/or a <Mono>remove</Mono> list. Both are optional —
                you can add without removing and vice versa. All tags are normalised to lowercase.
                Duplicate additions are silently ignored; removing a tag that doesn't exist is a no-op.
              </BodyText>
              <Code label="curl — add tags">
{`curl -X PATCH ${BASE}/files/analysis/a3f1c2d4.../tags \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"add": ["trojan", "virustotal"], "remove": []}'`}
              </Code>
              <Code label="curl — remove a tag">
{`curl -X PATCH ${BASE}/files/analysis/a3f1c2d4.../tags \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"add": [], "remove": ["manual"]}'`}
              </Code>
              <Code label="response">
{`{
  "sha256": "a3f1c2d4...",
  "tags": ["trojan", "virustotal"]   // updated list
}`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Malware Family ───────────────────────────── */}
        <Section>
          <SectionTitle>Malware Family</SectionTitle>
          <SectionText>
            Set or updated the malware family label on a sample, e.g. after manual analyst review of an
            auto-detected result.
          </SectionText>
          <EndpointGrid>

            <Endpoint method="PATCH" path="/api/files/analysis/{sha256}/family" description="Manually set or update a sample's malware family">
              <BodyText>
                Overrides the auto-detected <Mono>malware_family</Mono> value. The value cannot be empty.
              </BodyText>
              <Code label="curl">
{`curl -X PATCH ${BASE}/files/analysis/a3f1c2d4.../family \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"malware_family": "ToxicPanda V2"}'`}
              </Code>
              <Code label="response">
{`{
  "sha256": "a3f1c2d4...",
  "malware_family": "ToxicPanda V2"
}`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Search ───────────────────────────────────── */}
        <Section>
          <SectionTitle>Search</SectionTitle>
          <EndpointGrid>

            <Endpoint method="GET" path="/api/search/list" description="Browse latest analyses with optional filters">
              <BodyText>
                Returns the most recent analyses sorted by timestamp descending, paginated.
                All filter parameters are optional and stack with AND semantics.
              </BodyText>
              <Table>
                <thead>
                  <tr><Th>Parameter</Th><Th>Type</Th><Th>Description</Th></tr>
                </thead>
                <tbody>
                  <tr><Td><Mono>page</Mono></Td><Td>int</Td><Td>Page number (default: 1)</Td></tr>
                  <tr><Td><Mono>page_size</Mono></Td><Td>int</Td><Td>Results per page, max 100 (default: 10)</Td></tr>
                  <tr><Td><Mono>status</Mono></Td><Td>string</Td><Td>Filter by analysis_status (e.g. <Mono>completed</Mono>, <Mono>failed</Mono>)</Td></tr>
                  <tr><Td><Mono>packer</Mono></Td><Td>true|false</Td><Td>Filter by packer detection</Td></tr>
                  <tr><Td><Mono>frosting</Mono></Td><Td>true|false</Td><Td>Filter by APK frosting</Td></tr>
                  <tr><Td><Mono>tag</Mono></Td><Td>string (repeatable)</Td><Td>Filter by tag — repeat to match any of multiple tags: <Mono>?tag=virustotal&amp;tag=urlscan</Mono></Td></tr>
                  <tr><Td><Mono>minutes</Mono></Td><Td>int</Td><Td>Return only analyses from the last N minutes</Td></tr>
                </tbody>
              </Table>
              <Code label="curl — browse latest">
{`curl "${BASE}/search/list?page=1&page_size=20" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="curl — filter: completed samples from last hour">
{`curl "${BASE}/search/list?status=completed&minutes=60" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="curl — filter: multiple tags (OR)">
{`curl "${BASE}/search/list?tag=virustotal&tag=urlscan" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="POST" path="/api/search/analysis" description="Boolean field search">
              <BodyText>
                Search using <Mono>field:value</Mono> pairs with boolean operators.
                Values containing spaces must be quoted: <Mono>field:"my value"</Mono>.
                Hash fields (<Mono>sha256</Mono>, <Mono>md5</Mono>) use exact match;
                all others use case-insensitive partial match.
              </BodyText>
              <Table headers={['Operator', 'Symbol', 'Example']}>
                <tr><td>AND</td><td><Mono>&amp;</Mono> or space</td><td><Mono>package_name:com.evil &amp; tag:virustotal</Mono></td></tr>
                <tr><td>OR</td><td><Mono>|</Mono></td><td><Mono>malware_family:BankBot | malware_family:Cerberus</Mono></td></tr>
                <tr><td>NOT</td><td><Mono>NOT</Mono></td><td><Mono>status:completed &amp; NOT tag:research</Mono></td></tr>
              </Table>
              <Table headers={['Field', 'Match type', 'Example value']}>
                <tr><td><Mono>sha256</Mono></td><td>Exact</td><td><Mono>a3f1c2d4...</Mono></td></tr>
                <tr><td><Mono>md5</Mono></td><td>Exact</td><td><Mono>d41d8cd9...</Mono></td></tr>
                <tr><td><Mono>package_name</Mono></td><td>Partial, case-insensitive</td><td><Mono>com.example</Mono></td></tr>
                <tr><td><Mono>app_name</Mono></td><td>Partial, case-insensitive</td><td><Mono>"My App"</Mono></td></tr>
                <tr><td><Mono>malware_family</Mono></td><td>Partial, case-insensitive</td><td><Mono>BankBot</Mono></td></tr>
                <tr><td><Mono>tag</Mono></td><td>Exact, case-insensitive</td><td><Mono>virustotal</Mono></td></tr>
                <tr><td><Mono>status</Mono></td><td>Exact, case-insensitive</td><td><Mono>completed</Mono></td></tr>
                <tr><td><Mono>packer</Mono></td><td>Boolean</td><td><Mono>true</Mono> / <Mono>false</Mono></td></tr>
              </Table>
              <Code label="curl — AND: completed samples tagged virustotal">
{`curl -X POST ${BASE}/search/analysis \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "status:completed & tag:virustotal"}'`}
              </Code>
              <Code label="curl — OR: two malware families">
{`curl -X POST ${BASE}/search/analysis \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "malware_family:BankBot | malware_family:Cerberus"}'`}
              </Code>
              <Code label="curl — NOT: exclude a tag">
{`curl -X POST ${BASE}/search/analysis \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "status:completed & NOT tag:research"}'`}
              </Code>
              <Code label="curl — combined: package AND (tag OR tag)">
{`curl -X POST ${BASE}/search/analysis \\
  -H "X-API-Key: apk_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "package_name:\"com.evil\" & tag:virustotal | tag:urlscan"}'`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/search/verify/{sha256}" description="Check if an analysis exists">
              <Code label="curl">
{`curl ${BASE}/search/verify/a3f1c2d4... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`{ "exists": true, "document": { ... } }
{ "exists": false, "document": null }`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Pivots ───────────────────────────────────── */}
        <Section>
          <SectionTitle>Pivot Correlation</SectionTitle>
          <SectionText>
            Pivots let you discover relationships between samples — find every APK that shares a certificate,
            icon, permission set, or malware family with a sample you already know about.
          </SectionText>
          <EndpointGrid>

            <Endpoint method="GET" path="/api/pivots/icon-hash/{icon_hash}" description="APKs sharing the same icon">
              <Code label="curl">
{`curl "${BASE}/pivots/icon-hash/abc123...?limit=50" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/pivots/certificate/{cert_sha256}" description="APKs signed with the same certificate">
              <Code label="curl">
{`curl ${BASE}/pivots/certificate/def456... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/pivots/permission/{permission}" description="APKs requesting a specific permission">
              <Code label="curl">
{`curl "${BASE}/pivots/permission/android.permission.SEND_SMS" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/pivots/perm-hash/{perm_hash}" description="APKs with identical permission sets">
              <Code label="curl">
{`curl ${BASE}/pivots/perm-hash/ghi789... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/pivots/malware-family/{family}" description="All APKs in a malware family">
              <Code label="curl">
{`curl "${BASE}/pivots/malware-family/BankBot?limit=100" \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Artifacts ────────────────────────────────── */}
        <Section>
          <SectionTitle>Artifacts</SectionTitle>
          <SectionText>
            Artifacts are interesting files extracted from an APK during analysis — DEX files, native libraries,
            config files, and more. Each artifact has its own SHA256.
          </SectionText>
          <EndpointGrid>

            <Endpoint method="GET" path="/api/artifacts/apk/{sha256}" description="List artifacts for an APK">
              <Code label="curl">
{`curl ${BASE}/artifacts/apk/a3f1c2d4... \\
  -H "X-API-Key: apk_your_key_here"`}
              </Code>
              <Code label="response">
{`[
  {
    "filename": "classes.dex",
    "sha256": "e5f6...",
    "file_size": 204800,
    "original_path": "classes.dex"
  },
  ...
]`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/artifacts/apk/{sha256}/artifact/{artifact_sha256}/download" description="Download an artifact file">
              <Code label="curl">
{`curl "${BASE}/artifacts/apk/a3f1c2d4.../artifact/e5f6.../download" \\
  -H "X-API-Key: apk_your_key_here" \\
  -o classes.dex`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── API Key management ────────────────────────── */}
        <Section>
          <SectionTitle>API Key Management</SectionTitle>
          <WarnBox>
            These endpoints require a <strong>JWT Bearer token</strong> — they cannot be called with an API key.
          </WarnBox>
          <EndpointGrid>

            <Endpoint method="POST" path="/api/auth/api-keys" description="Generate a new API key">
              <BodyText>
                The full key is returned <strong>exactly once</strong> in the <Mono>api_key</Mono> field. Store it immediately.
              </BodyText>
              <Code label="curl">
{`curl -X POST ${BASE}/auth/api-keys \\
  -H "Authorization: Bearer <jwt_token>" \\
  -H "Content-Type: application/json" \\
  -d '{"label": "CI Pipeline"}'`}
              </Code>
              <Code label="response">
{`{
  "key_id": "uuid-...",
  "label": "CI Pipeline",
  "prefix": "apk_xxxx",
  "created_at": "2026-03-12T10:00:00",
  "api_key": "apk_FULL_KEY_SHOWN_ONCE"
}`}
              </Code>
            </Endpoint>

            <Endpoint method="GET" path="/api/auth/api-keys" description="List your API keys">
              <BodyText>Returns all keys with label, prefix, and dates — never the full key.</BodyText>
              <Code label="curl">
{`curl ${BASE}/auth/api-keys \\
  -H "Authorization: Bearer <jwt_token>"`}
              </Code>
            </Endpoint>

            <Endpoint method="DELETE" path="/api/auth/api-keys/{key_id}" description="Revoke an API key">
              <BodyText>Immediately invalidates the key. Any in-flight request using it will receive 401.</BodyText>
              <Code label="curl">
{`curl -X DELETE ${BASE}/auth/api-keys/uuid-... \\
  -H "Authorization: Bearer <jwt_token>"`}
              </Code>
            </Endpoint>

          </EndpointGrid>
        </Section>

        {/* ── Workflow examples ────────────────────────── */}
        <Section>
          <SectionTitle>Full workflow example</SectionTitle>
          <SectionText>
            A typical automation loop: submit a sample, wait for completion, then extract IOCs.
          </SectionText>
          <Code label="bash — submit and poll until complete">
{`#!/bin/bash
KEY="apk_your_key_here"
BASE="${BASE}"

# 1. Submit the APK
SHA=$(curl -s -X POST $BASE/files/upload \\
  -H "X-API-Key: $KEY" \\
  -F "file=@sample.apk" | jq -r '.file_id')

echo "SHA256: $SHA"

# 2. Poll until analysis completes
while true; do
  STATUS=$(curl -s "$BASE/files/analysis/$SHA/status" \\
    -H "X-API-Key: $KEY" | jq -r '.analysis_status')
  echo "Status: $STATUS"
  [ "$STATUS" = "completed" ] || [ "$STATUS" = "failed" ] && break
  sleep 5
done

# 3. Fetch the full report
curl -s "$BASE/files/analysis/$SHA" \\
  -H "X-API-Key: $KEY" | jq '{
    sha256: .sha256,
    family: .malware_family,
    package: .package_name,
    certificates: [.certificates[].sha256],
    suspicious_permissions: [.permissions[] | select(.suspicious) | .permission]
  }'`}
          </Code>
        </Section>

      </ContentContainer>
    </PageContainer>
  );
};

export default ApiDocs;
